import { isValid as isValidDate } from 'date-fns'
import { format, OptionsWithTZ, utcToZonedTime } from 'date-fns-tz'
import { formatPhoneNumber as fPN } from 'react-phone-number-input'

import { TimeZone } from 'config/localData'

const defaultFormats = {
	datetime: 'E, MMM d, yyyy, h:mm a',
	datetimeShort: 'M/d/yyyy, h:mm a',
	date: 'E, MMM d, yyyy',
	dateShort: 'M/d/yyyy',
	time: 'h:mm a',
}
export type DefaultDateFormat = keyof typeof defaultFormats
export interface FormatDateData {
	date?: string | number | Date | null
	formatString?: string
	defaultFormat?: DefaultDateFormat
	options?: OptionsWithTZ
	shiftTimezone?: TimeZone
}
export const formatDate = (data: FormatDateData) => {
	const { date, formatString, defaultFormat, options, shiftTimezone } = data
	let defaultFormatString = defaultFormat ? defaultFormats[defaultFormat] : undefined
	if (options?.timeZone && defaultFormatString) {
		defaultFormatString += ' zzz'
	}
	const formatStringToUse = formatString ?? defaultFormatString

	if (!formatStringToUse || date === undefined || date === null || date === '') {
		return ''
	}

	let normalizedDate: Date | null = null

	if (date instanceof Date) {
		normalizedDate = date
	} else if (typeof date === 'number') {
		if (!Number.isFinite(date) || date < 0) {
			return ''
		}
		const parsed = new Date(date)
		if (!isValidDate(parsed)) {
			return ''
		}
		normalizedDate = parsed
	} else if (typeof date === 'string') {
		const trimmed = date.trim()
		if (!trimmed) {
			return ''
		}
		const parsed = new Date(trimmed)
		if (!isValidDate(parsed)) {
			return ''
		}
		normalizedDate = parsed
	} else if (typeof date === 'object') {
		const maybeTimestamp: any = date
		if (typeof maybeTimestamp.toDate === 'function') {
			const parsed = maybeTimestamp.toDate()
			if (!isValidDate(parsed)) {
				return ''
			}
			normalizedDate = parsed
		} else if (typeof maybeTimestamp.toMillis === 'function') {
			const millis = maybeTimestamp.toMillis()
			if (!Number.isFinite(millis) || millis < 0) {
				return ''
			}
			const parsed = new Date(millis)
			if (!isValidDate(parsed)) {
				return ''
			}
			normalizedDate = parsed
		} else {
			return ''
		}
	} else {
		return ''
	}

	if (!normalizedDate) {
		return ''
	}

	const zonedDate = shiftTimezone ? utcToZonedTime(normalizedDate, shiftTimezone) : normalizedDate

	if (!isValidDate(zonedDate)) {
		return ''
	}

	return format(zonedDate, formatStringToUse, options)
}

export const objectOfBoolsToArray = (object?: { [key: string]: boolean }) => {
	if (!object) {
		return []
	}
	const arrayToReturn = []
	for (const key in object) {
		if (object[key]) {
			arrayToReturn.push(key)
		}
	}
	return arrayToReturn
}

export const formatPhoneNumber = (phone?: string, phoneExt?: string) => {
	if (!phone) {
		return ''
	}

	return `${fPN(phone)}${phoneExt ? ` ext. ${phoneExt}` : ''}`
}

export const firstCharacterToUpperCase = (s: string) => s.slice(0, 1).toUpperCase() + s.slice(1);

export const formatPurchaseRequisitionNumber = (
	prNumber?: number | string | null,
) => (prNumber === undefined || prNumber === null || prNumber === '' ? '' : `P.O. ${prNumber}`)

export const toUSD = (() => {
	const formatter = new Intl.NumberFormat('en-us', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
	})
	return (value: any) => {
		if (typeof value !== 'number') return value
		return formatter.format(value)
	}
})()
